<template>
  <div class="px-5 max-h-[32rem] overflow-x-hidden pb-5">
    <!-- Show Loading Indicator While Data is Fetching -->
    <div v-if="isLoading" class="flex justify-center items-center h-40">
      <VueSpinnerIos size="40px" />
    </div>
    <div v-else>
      <form @submit.prevent="onSubmit">
        <!-- Profile Information -->
        <div class="flex gap-4 items-center">
          <div class="w-full flex flex-col gap-2">
            <label for="name" class="w-full text-right">نام:</label>
            <input
              v-model="form.name"
              disabled
              type="text"
              name="name"
              class="input w-full cursor-not-allowed"
            />
          </div>
          <div class="w-full flex flex-col gap-2">
            <label for="phone" class="w-full text-right">تلفن:</label>
            <input
              v-model="form.phone"
              disabled
              type="text"
              name="phone"
              class="input w-full cursor-not-allowed"
            />
          </div>
        </div>
        <div class="flex mobile:flex-col-reverse tablet:flex-col">
          <!-- Attribute Values -->
          <div
            class="flex mt-5 flex-col gap-4 w-[55%] mobile:w-full tablet:w-full shrink-0"
          >
            <div
              class="w-full flex flex-col gap-2"
              v-for="attr in data?.attribute_values || []"
              :key="attr.id"
            >
              <label for="responsible" class="w-96 text-right mr-2">
                {{ attr.attribute_lead?.name }}:
              </label>
              <input
                v-model="form.attributes[attr.id]"
                type="text"
                name="responsible"
                class="input w-full"
              />
            </div>
          </div>

          <div
            class="w-[45%] mobile:w-full tablet:w-full shrink-0 flex flex-col mt-4 gap-2 mr-2 mobile:mr-0 tablet:mr-0 mobile:mt-4 tablet:mt-4"
          >
            <div class="flex flex-col justify-center items-center">
              <button
                @click.prevent="handleLoadOnlineInfo"
                :disabled="onlineInfoLoading"
                class="text-white border rounded-lg transition-colors bg-blue-primary hover:bg-blue-primary-darker py-2 px-4 mt-5 flex gap-2 justify-center w-full"
              >
                <span>نمایش اطلاعات آنلاین</span>
                <Icon
                  :icon="
                    onlineInfoLoading
                      ? 'line-md:loading-loop'
                      : isOnlineInfoVisible && liveInfo
                        ? 'mdi-light:chevron-up'
                        : 'mdi-light:chevron-down'
                  "
                  width="24"
                  height="24"
                />
              </button>

              <!-- Animated container -->
              <div
                class="w-full overflow-hidden transition-all duration-300 ease-in-out"
                :class="[
                  isOnlineInfoVisible && liveInfo ? 'max-h-[800px]' : 'max-h-0',
                ]"
              >
                <!-- Content container -->
                <div
                  class="py-4 bg-blue-primary bg-opacity-10 border-blue-primary-darker border border-t-0 rounded-b-lg"
                >
                  <div
                    class="flex flex-col items-center justify-center gap-4 px-4"
                  >
                    <p class="w-full text-center">
                      نام: {{ liveInfo?.name || "-" }}
                    </p>
                    <p class="w-full text-center">
                      نام خانوادگی: {{ liveInfo?.lastName || "-" }}
                    </p>
                    <p class="w-full text-center">
                      شماره موبایل: {{ liveInfo?.mobile || "-" }}
                    </p>
                    <p class="w-full text-center">
                      آیدی اینستاگرام: {{ liveInfo?.instagramUsername || "-" }}
                    </p>
                    <p class="w-full text-center">
                      تعداد دستورات: {{ liveInfo?.commandsCount || "-" }}
                    </p>
                    <p class="w-full text-center">
                      دسته بندی: {{ liveInfo?.category || "-" }}
                    </p>
                    <p class="w-full text-center">
                      تعداد فالور: {{ liveInfo?.follower_range || "-" }}
                    </p>
                    <p class="w-full text-center">
                      اشتراک کاربر:
                      {{ liveInfo?.userSubscription ? "دارد" : "ندارد" }}
                    </p>

                    <template v-if="liveInfo?.userSubscription">
                      <p class="w-full text-center">
                        تاریخ فعالسازی:
                        {{ liveInfo.userSubscription.creationDate }}
                      </p>
                      <p class="w-full text-center">
                        تاریخ انقضا:
                        {{ liveInfo.userSubscription.expirationDate }}
                      </p>
                      <p class="w-full text-center">
                        فعال است؟:
                        {{ liveInfo.userSubscription.isActive ? "بله" : "خیر" }}
                      </p>
                      <p class="w-full text-center">
                        شماره سفارش: {{ liveInfo.userSubscription.orderId }}
                      </p>
                      <p class="w-full text-center">
                        نوع پکیج: {{ liveInfo.userSubscription.package }}
                      </p>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <div class="w-full">
              <p class="font-bold">تغییر ستون:</p>
              <select
                name="pipeline"
                id="pipeline"
                v-model="selectedPipelineId"
                class="w-full mt-2 border rounded-lg focus:border-blue-300 transition-colors p-2 focus:outline-none active:outline-none bg-white"
                @change="handlePipelineChange"
              >
                <option
                  v-for="pipeline in pipelinesArray || []"
                  :key="pipeline.id"
                  :value="pipeline.id"
                >
                  {{ pipeline?.name }}
                </option>
              </select>
            </div>
            <hr class="my-4 mobile:hidden" />
            <div class="flex flex-col gap-2">
              <p for="tags" class="font-bold">تگ ها:</p>
              <VueSelect
                :options="options"
                dir="rtl"
                placeholder="تگ ها را انتخاب کنید"
                v-model="selectedTag"
              />
              <div class="flex flex-wrap gap-1">
                <button
                  @click="handleTagDelete(tag, $event)"
                  v-for="tag in data?.tags || []"
                  :key="tag.id"
                  class="rounded-full bg-blue-primary text-white transition-colors hover:bg-red-500 py-1 px-2 text-xs my-[2px]"
                >
                  <span class="select-none">{{ tag?.name }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="isSubmitting"
          class="text-white border rounded-lg transition-colors bg-green-500 hover:bg-green-600 py-2 px-4 mt-5"
        >
          {{ isSubmitting ? "در حال ذخیره..." : "ذخیره تغییرات" }}
        </button>
      </form>
      <div class="w-full mt-8">
        <p class="text-slate-500 text-xs">یادداشت ها</p>
        <div class="flex flex-col items-start gap-5 mt-4">
          <NoteTemplate
            v-for="note in data?.notes"
            :key="note.id"
            :note="note"
          />
        </div>
        <div>
          <div class="flex gap-2 mt-5 flex-wrap">
            <button
              class="text-nowrap bg-blue-primary py-2 px-4 text-white rounded transition-colors hover:bg-blue-primary-darker mobile:text-sm mobile:py-1 mobile:px-2"
              @click="handleNoteBtnClick('ارسال لینک خرید')"
            >
              ارسال لینک خرید
            </button>
            <button
              class="text-nowrap bg-blue-primary py-2 px-4 text-white rounded transition-colors hover:bg-blue-primary-darker mobile:text-sm mobile:py-1 mobile:px-2"
              @click="handleNoteBtnClick('عدم پاسخگویی')"
            >
              عدم پاسخگویی
            </button>
            <button
              class="text-nowrap bg-blue-primary py-2 px-4 text-white rounded transition-colors hover:bg-blue-primary-darker mobile:text-sm mobile:py-1 mobile:px-2"
              @click="handleNoteBtnClick('نیاز به زمان دارد')"
            >
              نیاز به زمان دارد
            </button>
            <button
              class="text-nowrap bg-blue-primary py-2 px-4 text-white rounded transition-colors hover:bg-blue-primary-darker mobile:text-sm mobile:py-1 mobile:px-2"
              @click="handleNoteBtnClick('نیاز به تایید کارفرما دارد')"
            >
              نیاز به تایید کارفرما دارد
            </button>
            <button
              class="text-nowrap bg-blue-primary py-2 px-4 text-white rounded transition-colors hover:bg-blue-primary-darker mobile:text-sm mobile:py-1 mobile:px-2"
              @click="handleNoteBtnClick('قیمت بالاست')"
            >
              قیمت بالاست
            </button>
            <button
              class="text-nowrap bg-blue-primary py-2 px-4 text-white rounded transition-colors hover:bg-blue-primary-darker"
              @click="handleNoteBtnClick('پیامک ارسال شد')"
            >
              پیامک ارسال شد
            </button>
          </div>
          <textarea
            v-model="noteDesc"
            placeholder="یادداشتت رو اینجا بنویس..."
            class="w-full border focus:border-blue-300 focus:outline-none py-2 px-4 text-sm rounded-lg min-h-[100px] mt-5"
          ></textarea>
          <button
            class="p-2 border-slate-300 border rounded-lg bg-white text-blue-primary transition-colors hover:bg-blue-primary hover:text-white hover:border-blue-primary"
            @click="handleAddNote"
          >
            افزودن
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  getLeadData,
  getOnlineInfo,
  getTags,
  postNote,
  postTagForLead,
  removeTag,
  updateLead,
} from "@/services/api/apiServiceLeads";
import { computed, onMounted, reactive, ref, watch, defineEmits } from "vue";
import { useToast } from "vue-toastification";
import { VueSpinnerIos } from "vue3-spinners";
import { useStore } from "vuex";
import VueSelect from "vue3-select-component";
import { useMutation } from "@tanstack/vue-query";
import Swal from "sweetalert2";
import NoteTemplate from "@/components/ui/modalContents/NoteTemplate.vue";
import { Icon } from "@iconify/vue";

const store = useStore();
const toast = useToast();
const emit = defineEmits(["close"]);
const isSubmitting = ref(false);
const noteDesc = ref("");

// Mutation for adding a tag
const { mutate: postTag } = useMutation({
  mutationFn: postTagForLead,
  onSuccess: async () => {
    Swal.fire({
      icon: "success",
      title: "تگ جدید با موفقیت اضافه شد!",
      showConfirmButton: false,
      timer: 1500,
    });
    const pipelineId = data.value?.pipeline?.id;
    const leadId = data.value?.id;
    const tag = selectedTag.value;
    if (data.value?.tags) {
      data.value.tags.push(tag);
    }
    store.commit("leadsSlice/addNewTagToLead", { pipelineId, leadId, tag });
  },
  onError: (error) => {
    console.error("Error adding tag:", error);
    Swal.fire({
      icon: "error",
      title: "عملیات با شکست مواجه شد.",
      showConfirmButton: false,
      timer: 1500,
    });
  },
});

// Mutation for deleting a tag
const { mutate: deleteTag } = useMutation({
  mutationFn: removeTag,
  onSuccess: () => {
    if (data.value?.tags) {
      data.value.tags = data.value.tags.filter(
        (tag) => tag.id !== clickedTagId,
      );
    }
    Swal.fire({
      icon: "success",
      title: "تگ با موفقیت حذف شد!",
      showConfirmButton: false,
      timer: 1500,
    });
    const pipelineId = data.value?.pipeline?.id;
    const leadId = data.value?.id;
    store.commit("leadsSlice/removeTagFromLead", {
      pipelineId,
      leadId,
      tagId: clickedTagId,
    });
  },
  onError: (err) => {
    console.error(err);
    Swal.fire({
      icon: "error",
      title: "عملیات با شکست مواجه شد.",
      showConfirmButton: false,
      timer: 1500,
    });
  },
});

// Mutation for updating lead data
const { mutate: updateLeadMutate } = useMutation({
  mutationFn: updateLead,
  onSuccess: () => {
    store.dispatch("leadsSlice/fetchLeadsForPipelines");
    Swal.fire({
      icon: "success",
      title: "عملیات با موفقیت انجام شد.",
      showConfirmButton: false,
      timer: 1500,
    });
    emit("close");
  },
  onError: (err) => {
    console.error(err);
    Swal.fire({
      icon: "error",
      title: "عملیات با شکست مواجه شد.",
      showConfirmButton: false,
      timer: 1500,
    });
  },
});

const { mutate: addNote } = useMutation({
  mutationFn: postNote,
  onSuccess: () => {
    Swal.fire({
      icon: "success",
      title: "عملیات با موفقیت انجام شد.",
      showConfirmButton: false,
      timer: 1500,
    });
    fetchData(id.value);
  },
  onError: () => {
    Swal.fire({
      icon: "error",
      title: "عملیات با شکست مواجه شد.",
      showConfirmButton: false,
      timer: 1500,
    });
  },
});

// Fetch data when the component is mounted
onMounted(() => {
  fetchData(id.value);
});

const id = computed(() => store.state.leadsSlice.selectedLeadItemId);
const data = ref(null);
const tags = ref([]);
const isLoading = ref(true);
const selectedTag = ref(null);
const selectedPipelineId = ref(null);
const onlineInfoLoading = ref(false);
const isOnlineInfoVisible = ref(false);
const liveInfo = ref(null);

watch(data, (newValue) => {
  selectedPipelineId.value = newValue?.pipeline?.id || null;
});

const options = computed(() => {
  return (
    tags.value?.data
      ?.filter(
        (tag) =>
          !data.value?.tags?.some((selectedTag) => selectedTag.id === tag.id),
      )
      ?.map((tag) => ({ label: tag.name, value: tag })) || []
  );
});

const pipelinesArray = computed(() => store.state.leadsSlice.pipeLines || []);

let clickedTagId = null;

// Watch for changes in selected tags
watch(selectedTag, (newVal) => {
  if (newVal) {
    const payload = { tagId: newVal.id, leadId: id.value };
    postTag(payload);
  }
});

// Fetch data when the component is mounted
async function fetchData(id) {
  try {
    isLoading.value = true; // Start loading
    const response = await getLeadData(id);
    data.value = response;
    tags.value = await getTags();
  } catch (err) {
    console.error(err);
    toast.error("دریافت اطلاعات لید با مشکل مواجه شد.");
  } finally {
    isLoading.value = false; // End loading
  }
}

const handleLoadOnlineInfo = async () => {
  isOnlineInfoVisible.value = !isOnlineInfoVisible.value;

  if (isOnlineInfoVisible.value) {
    onlineInfoLoading.value = true;
    try {
      liveInfo.value = await getOnlineInfo(data.value?.id);
      console.log(liveInfo.value);
    } catch (err) {
      console.err(err);
    } finally {
      onlineInfoLoading.value = false;
    }
  }
};

// Handle tag deletion
async function handleTagDelete(tag, event) {
  event.preventDefault(); // Prevent the default action (form submission)
  event.stopPropagation(); // Stop the event from propagating to the parent elements

  clickedTagId = tag.id;
  const result = await Swal.fire({
    title: `تگ "${tag.name}" حذف خواهد شد`,
    text: "میخواهید ادامه دهید؟",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#5D87FF",
    cancelButtonColor: "#d33",
    confirmButtonText: "اره پاکش کن",
    cancelButtonText: "لغو",
  });

  if (result.isConfirmed) {
    deleteTag({ tagId: tag.id, leadId: data.value?.id });
  }
}

// Define the form model
const form = reactive({
  name: "",
  phone: "",
  attributes: {}, // Initialize as an empty object
});

// Update form.attributes when data is fetched
watch(
  data,
  (newData) => {
    if (newData) {
      form.name = newData.contact?.name || "";
      form.phone = newData.contact?.phone || "";
      form.attributes =
        newData.attribute_values?.reduce((acc, attr) => {
          acc[attr.id] = attr.value;
          return acc;
        }, {}) || {};
    }
  },
  { immediate: true },
);

// Handle form submission
const onSubmit = async () => {
  if (!data.value || isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    // Update data.value.attribute_values
    if (data.value.attribute_values) {
      data.value.attribute_values.forEach((attr) => {
        attr.value = form.attributes[attr.id] || "";
      });
    }

    // Transform attribute_values into the desired object format
    const attributeValuesObject =
      data.value.attribute_values?.reduce((acc, av, index) => {
        acc[index + 1] = av.value; // Use index + 1 as the key
        return acc;
      }, {}) || {};

    // Construct the payload
    const payload = {
      contact: {
        ...data.value.contact,
        name: data.value.contact?.name || "",
        phone: data.value.contact?.phone || "",
      },
      attribute_values: attributeValuesObject,
      // pipeline_id: selectedPipelineId.value,
    };

    await updateLeadMutate({ payload, leadId: data.value.id });
  } catch (error) {
    console.error("Error updating lead:", error);
    toast.error("Failed to update lead.");
  } finally {
    isSubmitting.value = false;
  }
};

const { mutate: updateLeadPipeline } = useMutation({
  mutationFn: updateLead,
  onSuccess: (res) => {
    localPipelineChange(res.data.id);
    data.value.id = res.data.id;
    Swal.fire({
      icon: "success",
      title: "عملیات با موفقیت انجام شد.",
      showConfirmButton: false,
      timer: 1500,
    });
  },
  onError: (err) => {
    // Reset the selectedPipelineId on error
    selectedPipelineId.value = data.value?.pipeline?.id;
    Swal.fire({
      icon: "error",
      title: "عملیات با شکست مواجه شد.",
      showConfirmButton: false,
      timer: 1500,
    });
    console.error(err);
  },
});

const handlePipelineChange = () => {
  const payload = {
    pipeline_id: selectedPipelineId.value,
  };
  updateLeadPipeline({ payload, leadId: data.value.id });
};

const localPipelineChange = (newLeadId) => {
  const fromPipelineId = data.value?.pipeline?.id;
  const toPipelineId = selectedPipelineId.value;
  const leadId = data.value?.id;

  // Update the local data's pipeline ID
  if (data.value && data.value.pipeline) {
    data.value.pipeline.id = toPipelineId;
  }

  const payload = {
    fromPipelineId,
    toPipelineId,
    leadId,
    newLeadId,
  };
  store.commit("leadsSlice/updateLeadPipeline", payload);
};

const handleAddNote = () => {
  if (noteDesc.value) {
    const payload = {
      description: noteDesc.value,
      lead_id: id.value,
    };
    addNote(payload);
  }
  toast.error("یادداشت نمی تواند خالی باشد.");
};

const handleNoteBtnClick = (description) => {
  const payload = {
    description,
    lead_id: id.value,
  };
  addNote(payload);
};
</script>

<style scoped>
.is-invalid {
  border-color: red;
}
</style>

<style scoped>
.is-invalid {
  border-color: red;
}
</style>
